import React, { FC } from 'react';
import { css } from '@emotion/core';
import { SloDTO } from '@atlassiansox/phobos-client-ts';

import { useSLOGraphController } from '@/controllers';
import { Item, HeroNumber, KeyValue, useActions } from '@/components/primitive';
import { grid, twoColumnGrid, SPACE_COZY } from '@/styles';
import { formatDateForDisplay } from '@/utils';
import { DateDisplayFormats } from '@/constants';
import { SLO } from '@/ui';

import { formatQualityScoreResult, getHeroNumberColorStyles } from '../new-slo-views/common/utils';

import LargeGraph from './graph/large';
import SLOCardHeader from './header';
import {
  getErrorBudget,
  formatPerformanceResult,
  formatObjectiveResult,
  formatErrorBudget,
} from './utils';
import * as styles from './large-styles';

type MetricsProps = Pick<SloDTO, 'performance' | 'objective' | 'quality'> & {
  isSkeleton?: boolean;
};
const Metrics: FC<MetricsProps> = ({ performance, objective, isSkeleton, quality }) => {
  const result = performance && performance.result;
  const isSLOQualityAvailable = !!(
    quality &&
    quality.resultItemsCount &&
    quality.resultItemsCount > 0
  );

  return (
    <>
      <div
        css={[
          grid,
          twoColumnGrid,
          css`
            gap: ${SPACE_COZY}px;
            margin-bottom: ${SPACE_COZY}px;
          `,
        ]}
      >
        <Item title="SLI">
          <HeroNumber
            isSkeleton={isSkeleton}
            css={getHeroNumberColorStyles({ performance, objective })}
          >
            {formatPerformanceResult(performance && performance.result)}
          </HeroNumber>
        </Item>
        <Item title="SLO">
          <HeroNumber isSkeleton={isSkeleton}>{formatObjectiveResult(objective)}</HeroNumber>
        </Item>
        <Item title="Error Budget">
          <HeroNumber isSkeleton={isSkeleton}>
            {formatErrorBudget(getErrorBudget(result, objective))}
          </HeroNumber>
        </Item>
        {quality && (
          <Item title="quality score">
            <HeroNumber
              isFraction
              isSkeleton={isSkeleton}
              isUnitHidden={!isSLOQualityAvailable}
              css={getHeroNumberColorStyles({ quality, objective })}
            >
              {formatQualityScoreResult(quality?.qualityScore)}
            </HeroNumber>
          </Item>
        )}
      </div>
      <KeyValue title="Latest Data">
        <code>
          {performance && performance.latestDataPointTimestamp
            ? formatDateForDisplay(
                performance.latestDataPointTimestamp,
                DateDisplayFormats.TIME_WITH_NUMERIC_DATE
              )
            : 'N/A'}
        </code>
      </KeyValue>
    </>
  );
};

type SLOCardProps = SLO & {
  isSkeleton?: boolean;
  isCompact?: boolean;
  isResponsive?: boolean;
};
const SLOCard: React.FC<SLOCardProps> = ({
  name,
  type,
  publicId,
  indicator,
  performance,
  objective,
  quality,
  isDraft,
  isSkeleton,
  isCompact,
  isResponsive,
}) => {
  const { onEdit } = useActions(publicId, name, 'slo');

  const graph = useSLOGraphController(objective, indicator, performance, publicId, quality, type);

  return (
    <div
      data-testid="slo-card-large"
      css={[
        styles.root,
        isCompact ? styles.compactCardStyles : '',
        isResponsive ? styles.responsiveCardStyles : '',
      ]}
    >
      {!isCompact && (
        <SLOCardHeader
          publicId={publicId}
          name={name}
          type={type}
          performance={performance}
          objective={objective}
          quality={quality}
          indicator={indicator}
          isDraft={isDraft}
          isAlertable={indicator && indicator.isAlertable}
          isSkeleton={isSkeleton}
        />
      )}

      <div css={[styles.contentGrid]}>
        <div>
          <Metrics
            performance={performance}
            objective={objective}
            isSkeleton={isSkeleton}
            quality={quality}
          />
        </div>
        <div css={graph.error && !(graph.warnings.length > 0) && [styles.errorGridItem]}>
          <LargeGraph
            warnings={graph.warnings}
            dataPoints={graph.dataPoints}
            error={graph.error}
            errorBudgetDataPoints={graph.errorBudgetDataPoints}
            errorBudgetDomain={graph.errorBudgetDomain}
            status={graph.status}
            onEdit={onEdit}
            publicId={publicId}
            selectionCallback={null}
            isResponsive={isResponsive}
          />
        </div>
      </div>
    </div>
  );
};

export default SLOCard;
